"use client";
import React, { useState, useEffect, useMemo } from "react";
import { Button } from "../../../components/ui/button";
import { Order } from "../types/order";
import { OrderCard } from "./OrderCard";
import axios from "axios";
import { useSelector } from "react-redux";
import { API_BASE_URL } from "../../../../src/App.js";
import { ChevronLeft, ChevronRight, FileText, Filter, X } from "lucide-react";
import { Loader } from "../../../components/Loader";

export const InvoicesPage: React.FC<{ userRole: "admin" | "dealer" | "employee"; userId: string }> = ({
  userRole,
  userId,
}) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [orderStatuses, setOrderStatuses] = useState<{ id: number; label: string; color: string }[]>([]);
  const maxResults = 6; 
    const [createdBy, setCreatedBy] = useState<string>("");
  const [customer, setCustomer] = useState<string>("");
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  
  const [users, setUsers] = useState<any[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const { authToken = "", id = "",role="" } = useSelector((state: any) => state.loginData || {});
    const fetchUsers = async () => {
  try {
          const res = await axios.get(`${API_BASE_URL}account/${id}/users`, {
            params: { firstResults: 0,maxResults:"all", role: "creator" },
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          });

    setUsers(res.data.users || []);
  } catch (error) {
    console.error("Error fetching users:", error);
  }
};

  const fetchCustomers = async () => {
  try {
          const res = await axios.get(`${API_BASE_URL}account/${id}/users`, {
            params: { firstResults: 0,maxResults:"all", role: "all" },
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          });

    setCustomers(res.data.users || []);
  } catch (error) {
    console.error("Error fetching customers:", error);
  }
};


  const fetchOrderStatuses = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}account/${id}/order-statuses`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setOrderStatuses(res.data.data || []);
    } catch (error) {
      console.error("Failed to fetch order statuses");
    }
  };

useEffect(() => {
  if (role === "admin") {
    fetchUsers();
    fetchCustomers();
  }
}, [role]);

useEffect(() => {
  if (id) fetchOrderStatuses();
}, [id]);

  // 🧠 Fetch orders from backend
  const fetchOrders = async (pageNum = 1) => {
    try {
      setLoading(true);
      const firstResults = (pageNum - 1) * maxResults;

      const res = await axios.get(`${API_BASE_URL}account/${id}/orders`, {
        params: { firstResults, maxResults, invoice: true,createdBy,
        customer,
        fromDate,
        toDate, },
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      setOrders(res.data.orders || []);
      setTotalCount(res.data.totalCount || 0);
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  };

  // 🔁 Fetch orders on mount and page change
  useEffect(() => {
    fetchOrders(1);
  }, []);

  useEffect(() => {
    fetchOrders(page);
  }, [page]);

  // 🔍 Client-side status filter — updates automatically when selectedStatus or orders change
  const filteredOrders = useMemo(() => {
    if (!selectedStatus) return orders;
    return orders.filter((o) => o.status === selectedStatus);
  }, [orders, selectedStatus]);

  // 🏷️ Per-status counts — auto-update whenever orders change (e.g. after refresh)
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { all: orders.length };
    orderStatuses.forEach((s) => {
      counts[s.label] = orders.filter((o) => o.status === s.label).length;
    });
    return counts;
  }, [orders, orderStatuses]);

  // 🧭 Pagination handlers
  const totalPages = Math.ceil(totalCount / maxResults);

  const handlePrev = () => {
    if (page > 1) {
      const newPage = page - 1;
      setPage(newPage);
      fetchOrders(newPage);
    }
  };

  const handleNext = () => {
    if (page < totalPages) {
      const newPage = page + 1;
      setPage(newPage);
      fetchOrders(newPage);
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 flex items-center gap-3">
            <FileText className="text-orange-600" size={36} />
            Invoices
          </h1>
          <p className="text-gray-500 text-sm mt-2">View and manage all your invoices efficiently</p>
        </div>
      </div>
      {/* Filters Section */}
      <div>
        {/* Mobile Filter Button */}
        <div className="lg:hidden mb-4">
          <Button
            onClick={() => setShowFilters(!showFilters)}
            className="w-full btn-primary flex items-center justify-center gap-2"
          >
            {showFilters ? (
              <>
                <X size={20} />
                Hide Filters
              </>
            ) : (
              <>
                <Filter size={20} />
                Show Filters
              </>
            )}
          </Button>
        </div>

        {/* Filters Container */}
        <div className={`bg-white border-2 border-gray-200 rounded-xl p-4 flex flex-col lg:flex-row lg:flex-wrap gap-3 items-end ${
          showFilters ? 'flex' : 'hidden lg:flex'
        }`}>

  {/* Created By */}
  {role === "admin" && (
    <div className="flex flex-col flex-1 min-w-0">
      <label className="text-sm font-medium text-gray-600 mb-1">Created By</label>
      <select
        value={createdBy}
        onChange={(e) => setCreatedBy(e.target.value)}
        className="border rounded-lg px-3 py-2 text-sm w-full"
      >
        <option value="">All</option>
        {users.map((user) => (
          <option key={user.id} value={user.username}>
            {user.username}
          </option>
        ))}
      </select>
    </div>
  )}

  {/* Customer */}
  {role !== "dealer" && (
    <div className="flex flex-col flex-1 min-w-0">
      <label className="text-sm font-medium text-gray-600 mb-1">Customer</label>
      <select
        value={customer}
        onChange={(e) => setCustomer(e.target.value)}
        className="border rounded-lg px-3 py-2 text-sm w-full"
      >
        <option value="">All</option>
        {customers.map((user) => (
          <option key={user.id} value={user.id}>
            {user.username}
          </option>
        ))}
      </select>
    </div>
  )}

  {/* From Date */}
  <div className="flex flex-col flex-1 min-w-0">
    <label className="text-sm font-medium text-gray-600 mb-1">From</label>
    <input
      type="date"
      value={fromDate}
      onChange={(e) => setFromDate(e.target.value)}
      className="border rounded-lg px-3 py-2 text-sm w-full"
    />
  </div>

  {/* To Date */}
  <div className="flex flex-col flex-1 min-w-0">
    <label className="text-sm font-medium text-gray-600 mb-1">To</label>
    <input
      type="date"
      value={toDate}
      onChange={(e) => setToDate(e.target.value)}
      className="border rounded-lg px-3 py-2 text-sm w-full"
    />
  </div>

  {/* Apply Button */}
  <div className="flex-shrink-0">
    <Button
      onClick={() => {
        setPage(1);
        fetchOrders(1);
      }}
      className="btn-primary flex items-center gap-2 whitespace-nowrap"
    >
      Apply
    </Button>
  </div>
        </div>
      </div>

      {/* Status Tab Buttons with live counts — recomputed via useMemo on every refresh */}
      <div className="flex flex-row gap-2 flex-wrap items-center mt-4">
        <button
          onClick={() => setSelectedStatus("")}
          className={`px-4 py-2 rounded-xl font-semibold text-sm transition-all ${
            selectedStatus === ""
              ? "btn-primary"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
          }`}
        >
          All <span className="ml-1 opacity-75">{statusCounts.all ?? 0}</span>
        </button>
        {orderStatuses.map((s) => (
          <button
            key={s.id}
            onClick={() => setSelectedStatus(s.label)}
            style={{
              backgroundColor: selectedStatus === s.label ? s.color : `${s.color}22`,
              color: selectedStatus === s.label ? "#fff" : s.color,
              border: `1.5px solid ${s.color}`,
              boxShadow: selectedStatus === s.label ? `0 4px 12px ${s.color}55` : "none",
            }}
            className="px-4 py-2 rounded-xl font-semibold text-sm transition-all"
          >
            {s.label} <span className="ml-1 opacity-75">{statusCounts[s.label] ?? 0}</span>
          </button>
        ))}
      </div>

      {/* Grid View - Desktop and Mobile Responsive */}
      <div className="mt-6">
        {filteredOrders.length === 0 ? (
          <div className="py-12 text-center bg-white rounded-xl border-2 border-gray-200">
            <FileText className="mx-auto mb-4 text-gray-300" size={48} />
            <p className="text-gray-600 font-medium text-lg">No invoices found</p>
            <p className="text-gray-400 text-sm mt-1">Try checking back later</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredOrders.map((order) => (
              <OrderCard
                key={order.id}
                order={order}
                refresh={() => fetchOrders(page)}
                onEdit={() => {}}
                orderStatuses={orderStatuses}
              />
            ))}
          </div>
        )}
      </div>

      {/* Enhanced Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 p-4 bg-white rounded-xl border-2 border-gray-200">
          <p className="text-sm font-medium text-gray-600">
            Showing <span className="font-bold text-[var(--sidebar-primary)]">{(page - 1) * maxResults + 1}</span> to{" "}
            <span className="font-bold text-[var(--sidebar-primary)]">{Math.min(page * maxResults, totalCount)}</span> of{" "}
            <span className="font-bold text-[var(--sidebar-primary)]">{totalCount}</span> invoices
          </p>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              disabled={page === 1}
              onClick={handlePrev}
              className="flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 border-2"
            >
              <ChevronLeft size={18} />
              <span className="hidden sm:inline">Previous</span>
            </Button>
            <div className="px-4 py-2.5 flex items-center gap-2 bg-gray-100 rounded-lg border-2 border-gray-200">
              <span className="text-sm font-bold text-gray-700">{page}</span>
              <span className="text-gray-400">/</span>
              <span className="text-sm font-bold text-gray-700">{totalPages}</span>
            </div>
            <Button
              variant="outline"
              disabled={page === totalPages}
              onClick={handleNext}
              className="flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 border-2"
            >
              <span className="hidden sm:inline">Next</span>
              <ChevronRight size={18} />
            </Button>
          </div>
        </div>
      )}

    </div>
  );
};

export default InvoicesPage;
